import {useGetSessionUser} from '../../core/use-get-session-user';
import {useUpdateDismissibleUserSetting} from '../../core/use-update-dismissible-user-setting';

import {CUE_CARD_COLLECTION} from '../consts/cue-card-collection';
import type {CueCardLocation, CueCard} from '../consts/cue-card-collection';

const getSettingName = (settingsKey: string) => `cuehide_${settingsKey}`;

/**
 * Returns list of all CUE cards that should be displayed on a page in the given CueCardLocation, or undefined if none.
 *
 * @param pagePath
 * @param location
 * @returns {undefined|T[]}
 */
export const useGetCueCardsForLocation = (
    pagePath: string,
    location: CueCardLocation
): CueCard[] | null | undefined => {
    const {user, isLoading} = useGetSessionUser();
    const dismissedSettings = user ? user.dismissedSettings : [];
    const completedWizards = user ? user.completedWizards : [];

    if (Object.keys(CUE_CARD_COLLECTION).includes(pagePath) && !isLoading) {
        // @ts-expect-error
        const cardsForLocation = CUE_CARD_COLLECTION[pagePath].cards
            .filter((card) => card.location === location)
            .filter((card) => !dismissedSettings.includes(getSettingName(card.settingsKey)))
            .filter((card) => !completedWizards.includes(card.settingsKey))
            .filter((card) => !card.adminOnly || (user && user.isAdministrator))
            .filter(
                (card) => !card.requiredParam || window.location.search.includes(card.requiredParam)
            )
            .filter((card) => !card.featureFlag || NutClientConfig[card.featureFlag]);
        if (cardsForLocation.length) {
            return cardsForLocation;
        }
    }

    return undefined;
};

export function useDismissCueCard(): {
    onDismiss?: (settingsKey: string) => void;
} {
    const {user} = useGetSessionUser();
    const updateDismissibleUserSetting = useUpdateDismissibleUserSetting();

    if (!user) {
        return {};
    }

    return {
        onDismiss: (settingsKey: string) => {
            const settingName = getSettingName(settingsKey);

            updateDismissibleUserSetting({
                userId: user.id,
                settingName: settingName,
                value: '1',
            });
        },
    };
}
